import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/lib/types/auth";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('auth', 'ProfileUtils');

export const fetchProfile = async (userId: string): Promise<Profile | null> => {
  try {
    logger.info(`Fetching profile for user: ${userId}`);
    
    if (!userId) {
      logger.error("No user ID provided for profile fetch");
      return null;
    }
    
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      logger.error(`Error fetching profile: ${error.message}`);
      return null;
    }

    // When a user first signs up, they might have a profile row but with null values
    logger.info(`Profile fetch RAW response: ${JSON.stringify(profile)}`);

    // Log more details about the profile to help with debugging
    if (profile) {
      logger.info(`Profile fetch successful: ${JSON.stringify({
        id: profile.id,
        hasFirstName: !!profile.first_name,
        hasLastName: !!profile.last_name,
        hasSchoolDistrict: !!profile.school_district,
        hasSchool: !!profile.school,
        hasPosition: !!profile.position,
        schoolDistrict: profile.school_district || 'null',
        school: profile.school || 'null',
        position: profile.position || 'null'
      })}`);
    } else {
      logger.warning("No profile found for user");
    }
    
    return profile;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`Exception in fetchProfile: ${errorMessage}`);
    return null;
  }
};

// New function to check if a profile is incomplete
export const isProfileIncomplete = (profile: Profile | null): boolean => {
  if (!profile) return true;
  
  return !profile.first_name || 
         !profile.last_name || 
         !profile.school_district || 
         !profile.school || 
         !profile.position;
};

export type IncompleteField = 'personal' | 'school' | 'position';

export interface ProfileCompletionStatus {
  isComplete: boolean;
  incompleteFields: IncompleteField[];
  percentComplete: number;
}

export const getProfileCompletionStatus = (profile: Profile | null): ProfileCompletionStatus => {
  if (!profile) {
    return {
      isComplete: false,
      incompleteFields: ['personal', 'school', 'position'],
      percentComplete: 0
    };
  }

  const incompleteFields: IncompleteField[] = [];
  let completedSections = 0;
  const totalSections = 3; // personal, school, position

  // Check personal info
  if (!profile.first_name || !profile.last_name) {
    incompleteFields.push('personal');
  } else {
    completedSections++;
  }

  // Check school info
  if (!profile.school_district || !profile.school) {
    incompleteFields.push('school');
  } else {
    completedSections++;
  }

  // Check position
  if (!profile.position) {
    incompleteFields.push('position');
  } else {
    completedSections++;
  }

  const percentComplete = Math.round((completedSections / totalSections) * 100);

  logger.info(`Profile completion status: ${JSON.stringify({
    incompleteFields,
    percentComplete,
    isComplete: incompleteFields.length === 0
  })}`);

  return {
    isComplete: incompleteFields.length === 0,
    incompleteFields,
    percentComplete
  };
};
